"use client";
import React, { useState } from "react";
import "./Input.css";

/**
 * Generic input component – now accepts an optional `width` prop.
 *
 * @param {string}  placeholder – placeholder text
 * @param {string}  value       – controlled value
 * @param {boolean|null} valid  – validity flag (adds .valid / .invalid class);
 *                                null = bewusst neutral (weder valid noch invalid,
 *                                z.B. FIN mit 13-16 Zeichen: koennte fertig sein)
 * @param {function} onChange   – callback receiving event-like { target: { value } }
 * @param {string}  type        – html input type ("text", "number", etc.)
 * @param {string|number} width – explicit width (e.g. 500 or "100%")
 * @param {function} onFocus    – optional focus handler
 * @param {function} onBlur     – optional blur handler
 */
function Input({
  placeholder = "FIN EINTRAGEN",
  value = "",
  valid,
  onChange,
  type = "text",
  className = "",
  width, // new: explicit width (number → px | string → raw)
  onFocus: propOnFocus,
  onBlur: propOnBlur,
  inputMode = "text",
  pattern,
  maxLength,
  autoCapitalize,
  autoCorrect,
  spellCheck,
  showValidationWhileFocused = false,
  errorMessage = "",
}) {
  const [isFocused, setIsFocused] = useState(false);

  // ————————————————————————————————————————————
  // Handlers
  // ————————————————————————————————————————————
  const handleChange = (event) => {
    let inputValue = event.target.value;

    // local number formatting (de-DE with thousand-separators)
    if (type === "number") {
      inputValue = inputValue.replace(/\D/g, "");
      if (inputValue) inputValue = Number(inputValue).toLocaleString("de-DE");
    }

    // Emit raw numeric string (no dots) for numbers, raw string otherwise
    onChange({ target: { value: inputValue.replace(/\./g, "") } });
  };

  // Ensure displayed value is formatted when type="number"
  const formattedValue =
    type === "number" && value
      ? Number(String(value).replace(/\./g, "")).toLocaleString("de-DE")
      : value;
  const hasValue = String(formattedValue ?? "").length > 0;
  const showValidationClass =
    hasValue && (showValidationWhileFocused || !isFocused);

  const handleFocus = (e) => {
    setIsFocused(true);
    propOnFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    propOnBlur?.(e);
  };

  // Width style helper ------------------------------------------------
  const style = {};
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  return (
    <>
      <input
        type={type === "number" ? "text" : type}
        inputMode={inputMode}
        pattern={pattern}
        className={`default-input ${
          errorMessage
            ? "default-input-has-error"
            : showValidationClass && valid != null
              ? valid
                ? "valid"
                : "invalid"
              : ""
        } ${className}`.trim()}
        placeholder={placeholder}
        value={formattedValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        maxLength={maxLength}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        spellCheck={spellCheck}
        aria-invalid={errorMessage ? true : undefined}
        style={style}
      />
      {errorMessage ? (
        <div className="default-input-error" role="alert">
          <svg
            className="default-input-error__icon"
            viewBox="0 0 16 16"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <circle cx="8" cy="8" r="8" fill="#940000" />
            <path
              d="M5.5 5.5l5 5M10.5 5.5l-5 5"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <span>{errorMessage}</span>
        </div>
      ) : null}
    </>
  );
}

export default Input;
